import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useGetOnboardingQuery,
  useUpdateOnboardingMutation,
} from '../../../store/api/onboardingApi';
import WizardCard from '../WizardCard';

// Business type options reuse the existing Settings list.
const BUSINESS_TYPES = [
  { value: '', label: 'Select a type (optional)' },
  { value: 'technology', label: 'Technology' },
  { value: 'ecommerce', label: 'E-Commerce' },
  { value: 'saas', label: 'SaaS / Software' },
  { value: 'consulting', label: 'Consulting' },
  { value: 'other', label: 'Other' },
];

const COUNTRIES = [
  { value: '', label: 'Select a country' },
  { value: 'IN', label: 'India' },
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'SG', label: 'Singapore' },
  { value: 'AE', label: 'United Arab Emirates' },
  { value: 'other', label: 'Other' },
];

const VOLUME_RANGES = [
  { value: '', label: 'Select a range' },
  { value: '0-1k', label: '$0 – $1,000' },
  { value: '1k-10k', label: '$1,000 – $10,000' },
  { value: '10k-50k', label: '$10,000 – $50,000' },
  { value: '50k+', label: '$50,000+' },
];

const isValidUrl = (value) => {
  try {
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    // eslint-disable-next-line no-new
    new URL(withProtocol);
    return true;
  } catch {
    return false;
  }
};

const BusinessProfile = () => {
  const navigate = useNavigate();
  const { data: state, isLoading, isError } = useGetOnboardingQuery();
  const [updateOnboarding, { isLoading: isSaving }] = useUpdateOnboardingMutation();

  const [form, setForm] = useState({
    name: '',
    website: '',
    type: '',
    country: '',
    volume: '',
  });
  const [errors, setErrors] = useState({});

  // Prefill from saved state when it arrives (resume).
  useEffect(() => {
    if (!state) return;
    setForm({
      name: state.business?.name || '',
      website: state.business?.website || '',
      type: state.business?.type || '',
      country: state.business?.country || '',
      volume: state.volume || '',
    });
  }, [state]);

  const change = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Business name is required';
    if (form.website.trim() && !isValidUrl(form.website.trim())) {
      next.website = 'Enter a valid URL, for example https://acme.com';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (patch) => {
    try {
      await updateOnboarding(patch).unwrap();
      navigate('/onboarding/use-cases');
    } catch (err) {
      toast.error(err?.message || 'Could not save. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    save({
      business: {
        name: form.name.trim(),
        website: form.website.trim(),
        type: form.type,
        country: form.country,
      },
      volume: form.volume,
      onboarding_status: 'business_done',
    });
  };

  // Skip records partial state and advances; the dashboard checklist surfaces
  // the unfinished item.
  const handleSkip = () => save({ onboarding_status: 'business_done' });

  // Loading skeleton (never a blank screen).
  if (isLoading) {
    return (
      <section className="wiz-card" aria-busy="true">
        <div className="wiz-skeleton" style={{ width: '70%', marginBottom: 12 }} />
        <div className="wiz-skeleton" style={{ width: '90%', marginBottom: 24 }} />
        <div className="wiz-skeleton block" style={{ marginBottom: 12 }} />
        <div className="wiz-skeleton block" style={{ marginBottom: 12 }} />
        <div className="wiz-skeleton block" />
      </section>
    );
  }

  return (
    <WizardCard
      title="Tell us about your business"
      subtitle="This helps us configure the right settings and stay compliant."
      primaryLabel="Continue →"
      onSubmit={handleSubmit}
      primaryDisabled={!form.name.trim()}
      primaryLoading={isSaving}
      onSkip={handleSkip}
      error={isError ? 'We could not load your saved details. You can still continue.' : undefined}
    >
      <div className="rd-field wiz-field">
        <label className="rd-label" htmlFor="biz-name">Business name</label>
        <input
          id="biz-name"
          type="text"
          value={form.name}
          onChange={change('name')}
          className={`rd-input ${errors.name ? 'has-error' : ''}`}
          placeholder="Demo Corp"
          autoFocus
        />
        {errors.name && <span className="rd-error">{errors.name}</span>}
      </div>

      <div className="wiz-field-row">
        <div className="rd-field wiz-field">
          <label className="rd-label" htmlFor="biz-type">Business type</label>
          <select id="biz-type" value={form.type} onChange={change('type')} className="rd-select">
            {BUSINESS_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="rd-field wiz-field">
          <label className="rd-label" htmlFor="biz-country">Country</label>
          <select id="biz-country" value={form.country} onChange={change('country')} className="rd-select">
            {COUNTRIES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rd-field wiz-field">
        <label className="rd-label" htmlFor="biz-website">
          Website <span className="rd-optional">(optional)</span>
        </label>
        <input
          id="biz-website"
          type="url"
          value={form.website}
          onChange={change('website')}
          className={`rd-input ${errors.website ? 'has-error' : ''}`}
          placeholder="https://yourbusiness.com"
        />
        {errors.website && <span className="rd-error">{errors.website}</span>}
      </div>

      <div className="rd-field wiz-field">
        <label className="rd-label" htmlFor="biz-volume">Expected monthly volume</label>
        <select id="biz-volume" value={form.volume} onChange={change('volume')} className="rd-select">
          {VOLUME_RANGES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <span className="rd-hint">
          Used to recommend the right plan for you. You can change this anytime.
        </span>
      </div>
    </WizardCard>
  );
};

export default BusinessProfile;
